import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaSave,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  createPartenaireKata,
  updatePartenaireKata,
} from "../../../services/partenairesKataApi";

import {
  KATA_GRADES,
  KATA_ROLE_OPTIONS,
  KATA_TYPES,
} from "../data/kataOptions";

import {
  getDepartmentsForRegion,
  getRegions,
} from "../utils/kataUtils";

import KataButton from "../ui/KataButton";
import ModalShell from "../ui/ModalShell";

const inputClassName =
  "h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-400/10 dark:border-white/10 dark:bg-slate-800 dark:text-white";

const textareaClassName =
  "min-h-32 w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold leading-6 text-slate-800 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-400/10 dark:border-white/10 dark:bg-slate-800 dark:text-white";

const createInitialForm = (partenaire, credentials) => ({
  nom: partenaire?.nom || "",
  prenom: partenaire?.prenom || "",
  region: partenaire?.region || "",
  departement: partenaire?.departement || "",
  club: partenaire?.club || "",
  passage_grade: partenaire?.passage_grade || "",
  kata: partenaire?.kata || "",
  role_recherche:
    partenaire?.role_recherche || "indifferent",
  mobilite: partenaire?.mobilite || "",
  disponibilites: partenaire?.disponibilites || "",
  content: partenaire?.content || "",
  tel: partenaire?.tel || "",
  mail: partenaire?.mail || credentials?.email || "",
  contact_phone_public:
    partenaire?.contact_phone_public ?? true,
  contact_email_public:
    partenaire?.contact_email_public ?? true,
  password: "",
  password_confirmation: "",
  new_password: "",
  new_password_confirmation: "",
  consent: Boolean(partenaire),
});

export default function PartenaireFormModal({
  mode = "create",
  partenaire,
  credentials,
  isOpen,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(() =>
    createInitialForm(partenaire, credentials)
  );

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(
        createInitialForm(partenaire, credentials)
      );
    }
  }, [isOpen, partenaire, credentials]);

  const regions = useMemo(() => getRegions(), []);

  const departments = useMemo(
    () => getDepartmentsForRegion(formData.region),
    [formData.region]
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "region"
        ? {
            departement: "",
          }
        : {}),
    }));
  };

  const validateForm = () => {
    const requiredFields = [
      ["nom", "Le nom est obligatoire."],
      ["prenom", "Le prénom est obligatoire."],
      ["region", "La région est obligatoire."],
      ["departement", "Le département est obligatoire."],
      ["club", "Le club est obligatoire."],
      [
        "passage_grade",
        "Le grade préparé est obligatoire.",
      ],
      ["content", "La description est obligatoire."],
      ["mail", "L’adresse email est obligatoire."],
    ];

    for (const [key, message] of requiredFields) {
      if (!String(formData[key] || "").trim()) {
        toast.error(message);
        return false;
      }
    }

    if (
      !formData.contact_phone_public &&
      !formData.contact_email_public
    ) {
      toast.error(
        "Vous devez rendre public au moins un moyen de contact."
      );

      return false;
    }

    if (
      formData.contact_phone_public &&
      !formData.tel.trim()
    ) {
      toast.error(
        "Renseignez un téléphone ou désactivez sa publication."
      );

      return false;
    }

    if (!isEdit) {
      if (formData.password.length < 8) {
        toast.error(
          "Le mot de passe doit contenir au moins 8 caractères."
        );

        return false;
      }

      if (
        formData.password !==
        formData.password_confirmation
      ) {
        toast.error(
          "Les deux mots de passe ne correspondent pas."
        );

        return false;
      }

      if (!formData.consent) {
        toast.error(
          "Vous devez accepter la publication de vos coordonnées."
        );

        return false;
      }
    }

    if (isEdit && formData.new_password) {
      if (formData.new_password.length < 8) {
        toast.error(
          "Le nouveau mot de passe doit contenir au moins 8 caractères."
        );

        return false;
      }

      if (
        formData.new_password !==
        formData.new_password_confirmation
      ) {
        toast.error(
          "Les deux nouveaux mots de passe ne correspondent pas."
        );

        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const data = {
      nom: formData.nom,
      prenom: formData.prenom,
      region: formData.region,
      departement: formData.departement,
      club: formData.club,
      passage_grade: formData.passage_grade,
      kata: formData.kata,
      role_recherche: formData.role_recherche,
      mobilite: formData.mobilite,
      disponibilites: formData.disponibilites,
      content: formData.content,
      tel: formData.tel,
      mail: formData.mail,
      contact_phone_public:
        formData.contact_phone_public,
      contact_email_public:
        formData.contact_email_public,
    };

    try {
      let response;

      if (isEdit) {
        response = await updatePartenaireKata(
          partenaire.id,
          credentials,
          {
            ...data,
            new_password:
              formData.new_password || undefined,
          }
        );
      } else {
        response = await createPartenaireKata({
          ...data,
          password: formData.password,
        });
      }

      toast.success(response.message);
      onSaved(response.data);
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEdit
          ? "Modifier votre annonce"
          : "Déposer une annonce"
      }
      subtitle={
        isEdit
          ? "Mettez à jour votre recherche de partenaire kata."
          : "Présentez votre recherche afin d’être contacté par d’autres judokas."
      }
      maxWidth="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="p-5 sm:p-7">
        <FormSection
          number="01"
          title="Votre identité sportive"
          description="Ces informations permettent de présenter clairement votre profil."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Prénom" required>
              <input
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                className={inputClassName}
                placeholder="Votre prénom"
              />
            </FormField>

            <FormField label="Nom" required>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className={inputClassName}
                placeholder="Votre nom"
              />
            </FormField>

            <FormField
              label="Club"
              required
              className="sm:col-span-2"
            >
              <input
                type="text"
                name="club"
                value={formData.club}
                onChange={handleChange}
                className={inputClassName}
                placeholder="Nom de votre club"
              />
            </FormField>

            <FormField label="Région" required>
              <select
                name="region"
                value={formData.region}
                onChange={handleChange}
                className={inputClassName}
              >
                <option value="">
                  Sélectionnez une région
                </option>

                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Département" required>
              <select
                name="departement"
                value={formData.departement}
                onChange={handleChange}
                className={inputClassName}
              >
                <option value="">
                  Sélectionnez un département
                </option>

                {departments.map((department) => (
                  <option
                    key={department.code}
                    value={department.code}
                  >
                    {department.code} ·{" "}
                    {department.departement}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </FormSection>

        <FormSection
          number="02"
          title="Votre recherche"
          description="Précisez le kata travaillé, le rôle recherché et votre objectif."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Grade préparé" required>
              <select
                name="passage_grade"
                value={formData.passage_grade}
                onChange={handleChange}
                className={inputClassName}
              >
                <option value="">
                  Sélectionnez un grade
                </option>

                {KATA_GRADES.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Kata travaillé">
              <select
                name="kata"
                value={formData.kata}
                onChange={handleChange}
                className={inputClassName}
              >
                <option value="">
                  Sélectionnez un kata
                </option>

                {KATA_TYPES.map((kata) => (
                  <option key={kata} value={kata}>
                    {kata}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Rôle recherché"
              className="sm:col-span-2"
            >
              <select
                name="role_recherche"
                value={formData.role_recherche}
                onChange={handleChange}
                className={inputClassName}
              >
                {KATA_ROLE_OPTIONS.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Disponibilités">
              <input
                type="text"
                name="disponibilites"
                value={formData.disponibilites}
                onChange={handleChange}
                className={inputClassName}
                placeholder="Ex. mercredi soir et week-end"
              />
            </FormField>

            <FormField label="Mobilité">
              <input
                type="text"
                name="mobilite"
                value={formData.mobilite}
                onChange={handleChange}
                className={inputClassName}
                placeholder="Ex. Gironde et départements voisins"
              />
            </FormField>

            <FormField
              label="Présentation, expérience et objectifs"
              required
              className="sm:col-span-2"
            >
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                className={textareaClassName}
                placeholder="Présentez votre niveau, le kata travaillé, vos échéances et le type de partenaire recherché…"
              />

              <p className="mt-2 text-right text-xs text-slate-400">
                {formData.content.length} / 3000 caractères
              </p>
            </FormField>
          </div>
        </FormSection>

        <FormSection
          number="03"
          title="Coordonnées"
          description="Vous choisissez les coordonnées qui seront visibles publiquement."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Adresse email" required>
              <input
                type="email"
                name="mail"
                value={formData.mail}
                onChange={handleChange}
                className={inputClassName}
                placeholder="vous@exemple.fr"
              />

              <VisibilityCheckbox
                name="contact_email_public"
                checked={formData.contact_email_public}
                onChange={handleChange}
                label="Afficher mon email sur l’annonce"
              />
            </FormField>

            <FormField label="Téléphone">
              <input
                type="tel"
                name="tel"
                value={formData.tel}
                onChange={handleChange}
                className={inputClassName}
                placeholder="06 00 00 00 00"
              />

              <VisibilityCheckbox
                name="contact_phone_public"
                checked={formData.contact_phone_public}
                onChange={handleChange}
                label="Afficher mon téléphone sur l’annonce"
              />
            </FormField>
          </div>
        </FormSection>

        <FormSection
          number="04"
          title="Protection de l’annonce"
          description="Ce mot de passe permettra uniquement de modifier ou supprimer cette annonce."
        >
          {!isEdit ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Mot de passe" required>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  className={inputClassName}
                  placeholder="8 caractères minimum"
                />
              </FormField>

              <FormField
                label="Confirmer le mot de passe"
                required
              >
                <input
                  type="password"
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  autoComplete="new-password"
                  className={inputClassName}
                  placeholder="Confirmez le mot de passe"
                />
              </FormField>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Nouveau mot de passe">
                <input
                  type="password"
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  className={inputClassName}
                  placeholder="Laissez vide pour ne pas le changer"
                />
              </FormField>

              <FormField label="Confirmer le nouveau mot de passe">
                <input
                  type="password"
                  name="new_password_confirmation"
                  value={
                    formData.new_password_confirmation
                  }
                  onChange={handleChange}
                  autoComplete="new-password"
                  className={inputClassName}
                  placeholder="Confirmez le nouveau mot de passe"
                />
              </FormField>
            </div>
          )}

          {!isEdit ? (
            <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
              <input
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="mt-1 h-5 w-5 rounded border-slate-300 text-orange-500 focus:ring-orange-400"
              />

              <span className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                J’accepte que les coordonnées sélectionnées
                soient publiées afin que des judokas puissent
                me contacter. Je pourrai supprimer mon annonce
                à tout moment.
              </span>
            </label>
          ) : null}
        </FormSection>

        <div className="sticky bottom-0 -mx-5 -mb-5 mt-8 flex flex-col gap-3 border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-white/10 dark:bg-slate-900/95 sm:-mx-7 sm:-mb-7 sm:flex-row sm:justify-end sm:px-7">
          <KataButton
            type="button"
            variant="secondary"
            onClick={onClose}
          >
            Annuler
          </KataButton>

          <KataButton
            type="submit"
            icon={isEdit ? FaSave : FaCheck}
            disabled={submitting}
          >
            {submitting
              ? "Enregistrement…"
              : isEdit
                ? "Enregistrer les modifications"
                : "Publier mon annonce"}
          </KataButton>
        </div>
      </form>
    </ModalShell>
  );
}

function FormSection({
  number,
  title,
  description,
  children,
}) {
  return (
    <section className="mb-8 last:mb-0">
      <div className="mb-5 flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-semibold text-white dark:bg-white dark:text-slate-950">
          {number}
        </span>

        <div>
          <h3 className="text-lg font-semibold text-slate-950 dark:text-white">
            {title}
          </h3>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        </div>
      </div>

      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-white/[0.03] sm:p-5">
        {children}
      </div>
    </section>
  );
}

function FormField({
  label,
  required,
  className = "",
  children,
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
        {label}
        {required ? (
          <span className="ml-1 text-orange-500">*</span>
        ) : null}
      </span>

      {children}
    </label>
  );
}

function VisibilityCheckbox({
  name,
  checked,
  onChange,
  label,
}) {
  return (
    <span className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400"
      />

      {label}
    </span>
  );
}